import { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Edit3, Trash2, X, Sparkles, ListChecks, UserPlus2,
  AlertTriangle, Pause, Play, Calendar, Tag, IndianRupee, Search, Wallet, PartyPopper, Download,
} from "lucide-react";

const SLOT_OPTIONS = [
  { id: "breakfast", label: "Breakfast" },
  { id: "lunch",     label: "Lunch" },
  { id: "snacks",    label: "Snacks / Tea" },
  { id: "dinner",    label: "Dinner" },
];

const TYPE_OPTIONS = [
  { id: "daily",   label: "Daily (single-day)" },
  { id: "weekly",  label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "term",    label: "Term" },
];

const emptyPlan = {
  name: "", type: "monthly", price: "", items_per_day: 1, description: "",
  slots: ["lunch"], non_veg_option: false, duration_days: 30, active: true,
  start_date: "", end_date: "", variant: "standard", term: "",
};

/** Inclusive day count between two YYYY-MM-DD strings. Returns null on bad input. */
function daysBetween(startStr, endStr) {
  if (!startStr || !endStr) return null;
  const s = new Date(startStr + "T00:00:00Z");
  const e = new Date(endStr + "T00:00:00Z");
  if (isNaN(s) || isNaN(e) || e < s) return null;
  return Math.round((e - s) / 86400000) + 1;
}

export default function AdminMealPlans() {
  const [tab, setTab] = useState("plans");
  const [typeFilter, setTypeFilter] = useState("all"); // all | daily | monthly
  const [plans, setPlans] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, draft: null, isNew: true });
  const [specialEditor, setSpecialEditor] = useState(null); // null or special object being edited
  const [assigner, setAssigner] = useState({ open: false, plan: null, studentId: "", paymentRef: "", note: "", endDate: "" });
  const [students, setStudents] = useState([]);
  const [schoolTerms, setSchoolTerms] = useState(["Term I", "Term II", "Term III"]);
  // Plan that was clicked "Assign" from the Plans tab; consumed by the Student actions tab
  const [pendingAssignPlanId, setPendingAssignPlanId] = useState("");
  
  // Subscription filters and search
  const [subSearch, setSubSearch] = useState("");
  const [subFilters, setSubFilters] = useState({ grade: "", plan: "", slot: "", term: "" });
  const [subStatusFilter, setSubStatusFilter] = useState("all"); // all | upcoming | active | expired | paused
  const [consumptionModal, setConsumptionModal] = useState({ open: false, data: null, loading: false });

  const load = async () => {
    setLoading(true);
    try {
      const [p, sp, s, st, sc] = await Promise.all([
        api.get("/meal-plans"),
        api.get("/specials").catch(() => ({ data: [] })),
        api.get(`/meal-plans/subscriptions/active`).catch(() => ({ data: [] })),
        api.get("/admin/users?role=student").catch(() => ({ data: [] })),
        api.get("/admin/school").catch(() => ({ data: { terms: ["Term I", "Term II", "Term III"] } })),
      ]);
      setPlans(p.data || []);
      setSpecials(sp.data || []);
      setSubs(s.data || []);
      setStudents(st.data || []);
      const terms = sc.data?.terms;
      if (Array.isArray(terms) && terms.length) setSchoolTerms(terms);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  
  // Load consumption data for a student.
  // - Upcoming plans: don't open (no data to show yet).
  // - Active plans: show today's per-slot status (default mode).
  // - Expired / Paused: show the full subscription-history view.
  const openConsumption = async (sp) => {
    const live = sp.live_status || sp.status;
    if (live === "upcoming") {
      toast.info("This subscription hasn't started yet — no consumption to show.");
      return;
    }
    const mode = (live === "expired" || live === "paused") ? "history" : "day";
    setConsumptionModal({ open: true, data: null, loading: true });
    try {
      const { data } = await api.get(`/meal-plans/subscriptions/${sp.student_plan_id}/consumption?mode=${mode}`);
      setConsumptionModal({ open: true, data: { ...data, mode }, loading: false });
    } catch (e) {
      toast.error(formatErr(e));
      setConsumptionModal({ open: false, data: null, loading: false });
    }
  };
  
  // Memoized filtered subscriptions
  const filteredSubs = useMemo(() => {
    const q = subSearch.toLowerCase();
    return subs.filter(sp => {
      if (q && !(
        (sp.student_name || "").toLowerCase().includes(q) ||
        (sp.student_id_display || "").toLowerCase().includes(q) ||
        (sp.plan_name || "").toLowerCase().includes(q) ||
        (sp.slots || []).some(s => s.toLowerCase().includes(q)) ||
        (sp.term || "").toLowerCase().includes(q)
      )) return false;
      if (subFilters.grade && sp.student_grade !== subFilters.grade) return false;
      if (subFilters.plan && sp.plan_name !== subFilters.plan) return false;
      if (subFilters.slot && !(sp.slots || []).some(s => s.toLowerCase().includes(subFilters.slot))) return false;
      if (subFilters.term && sp.term !== subFilters.term) return false;
      if (subStatusFilter !== "all" && (sp.live_status || sp.status) !== subStatusFilter) return false;
      return true;
    });
  }, [subs, subSearch, subFilters, subStatusFilter]);

  // Counts per live status (used to label chips)
  const subStatusCounts = useMemo(() => {
    const c = { all: subs.length, upcoming: 0, active: 0, expired: 0, paused: 0 };
    for (const sp of subs) {
      const s = sp.live_status || sp.status;
      if (c[s] !== undefined) c[s] += 1;
    }
    return c;
  }, [subs]);

  const savePlan = async () => {
    const d = editor.draft;
    if (!d.name?.trim()) return toast.error("Name is required");
    if (!d.slots?.length) return toast.error("Pick at least one slot");
    if (!d.start_date) return toast.error("Start date is required");
    if (!d.end_date) return toast.error("End date is required");
    if (d.end_date < d.start_date) return toast.error("End date must be on or after the start date");
    if (d.type === "term" && !d.term) return toast.error("Pick a term for this plan");
    const payload = {
      ...d,
      // Monthly subscriptions are billed by the school directly — price not collected here
      price: d.type === "monthly" ? 0 : (d.price ? Number(d.price) : 0),
      items_per_day: Number(d.items_per_day) || 1,
      duration_days: Number(d.duration_days) || 30,
      term: d.type === "term" ? d.term : null,
    };
    try {
      if (editor.isNew) await api.post("/meal-plans", payload);
      else await api.put(`/meal-plans/${d.id}`, payload);
      toast.success(editor.isNew ? "Plan created" : "Plan updated");
      setEditor({ open: false, draft: null, isNew: true });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deletePlan = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"? Existing subscriptions remain active until expiry.`)) return;
    try { await api.delete(`/meal-plans/${p.id}`); toast.success("Plan deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const submitAssign = async () => {
    const a = assigner;
    if (!a.plan || !(a.studentIds || []).length) return toast.error("Select at least one student");
    let success = 0, errors = [];
    for (const sid of a.studentIds) {
      try {
        await api.post("/meal-plans/admin-assign", { student_id: sid, plan_id: a.plan.id });
        success++;
      } catch (e) {
        const student = students.find(s => s.id === sid);
        errors.push(`${student?.name || sid}: ${e?.response?.data?.detail || "Failed"}`);
      }
    }
    if (success) toast.success(`Assigned ${success} student${success > 1 ? "s" : ""}`);
    if (errors.length) toast.error(`${errors.length} failed: ${errors[0]}`);
    setAssigner({ open: false, plan: null, studentIds: [], grades: [], note: "" });
    load();
    setTab("subs");
  };

  const pauseSub = async (sp, action) => {
    if (!window.confirm(`${action === "pause" ? "Pause" : "Resume"} subscription for ${sp.student_name}?`)) return;
    try { await api.patch(`/meal-plans/student-plan/${sp.student_plan_id}/${action}`); toast.success("Done"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const slotsBadge = (slots) => (slots || []).map(s => (
    <span key={s} className="px-2 py-0.5 rounded-full bg-cream-200 text-ink-700 text-[10px] font-bold uppercase mr-1">{s}</span>
  ));

  return (
    <AppShell variant="admin">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Meal Plans</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Subscription configuration</h1>
            <p className="text-sm text-ink-500 mt-2 max-w-2xl">Create / edit recurring meal plans (breakfast-only, lunch-only, full-day). Assign to a student manually when the parent paid the school directly.</p>
          </div>
          <button data-testid="meal-plan-new" onClick={() => setEditor({ open: true, isNew: true, draft: emptyPlan })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 self-start md:self-auto">
            <Plus size={16} /> New plan
          </button>
        </div>

        <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto no-scrollbar">
          {[
            { id: "plans", label: "Plans", icon: ListChecks, count: plans.length + specials.length },
            { id: "subs",  label: "Subscriptions", icon: Sparkles, count: subs.length },
            { id: "admin", label: "Student actions", icon: Search },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`mp-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 -mb-px text-sm font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${tab === t.id ? "text-brand border-b-2 border-brand" : "text-ink-500 hover:text-ink"}`}>
              <t.icon size={14} /> {t.label}
              {t.count !== undefined && <span className="px-2 py-0.5 rounded-full bg-cream-200 text-[10px]">{t.count}</span>}
            </button>
          ))}
        </div>

        {tab === "plans" && (
          <div className="flex gap-2 mb-5">
            {[
              { id: "all", label: "All" },
              { id: "daily", label: "Daily (Pre-orderable)" },
              { id: "monthly", label: "Monthly (Subscription)" },
              { id: "specials", label: "Specials/Festivals" },
            ].map(f => (
              <button key={f.id} onClick={() => setTypeFilter(f.id)} data-testid={`filter-${f.id}`}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${typeFilter === f.id ? "bg-ink text-white" : "bg-cream-200 text-ink-700 hover:bg-cream-300"}`}>
                {f.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-2xl p-8 text-center text-ink-500">Loading…</div>
        ) : tab === "plans" ? (
          (() => {
            const filteredPlans = typeFilter === "all" ? plans : typeFilter === "specials" ? [] : plans.filter(p => {
              if (typeFilter === "daily") return p.type === "daily";
              return p.type === "monthly" || p.type === "quarterly" || p.type === "weekly" || p.type === "term";
            });
            const filteredSpecials = typeFilter === "all" || typeFilter === "specials" ? specials : [];
            const combined = [...filteredPlans.map(p => ({ ...p, _kind: "plan" })), ...filteredSpecials.map(s => ({ ...s, _kind: "special" }))];
            return (
              <>
              {(typeFilter === "all" || typeFilter === "specials") && (
                <div className="flex justify-end mb-3">
                  <button data-testid="new-special-btn" onClick={() => setSpecialEditor({ title: "", description: "", lunch_date: "", image_url: "", items: [], price: "", max_orders: "", display_until: "", nutrition: null })}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-600 text-white font-semibold text-sm hover:bg-amber-700">
                    <Plus size={14} /> New special
                  </button>
                </div>
              )}
              {combined.length === 0 ? (
              <EmptyState onClick={() => setEditor({ open: true, isNew: true, draft: emptyPlan })} />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="meal-plan-grid">
                {combined.map(p => p._kind === "special" ? (
                  <div key={p.id} className="bg-white rounded-2xl shadow-soft overflow-hidden flex flex-col" data-testid={`special-card-${p.id}`}>
                    {p.image_url && <div className="h-32 overflow-hidden"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /></div>}
                    <div className="p-4 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 text-[9px] font-bold">SPECIAL</span>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${p.status === "open" ? "bg-emerald-200 text-emerald-800" : "bg-cream-200 text-ink-500"}`}>{p.status}</span>
                      </div>
                      <div className="font-heading text-base font-extrabold text-ink">{p.name}</div>
                      <div className="text-xs text-ink-500 mt-1">Date: {p.scheduled_date} · ₹{p.price} · {p.booked || 0} booked{p.capacity ? ` / ${p.capacity}` : ""}</div>
                      {p.display_until && <div className="text-[10px] text-amber-700 mt-1">Orders until: {p.display_until}</div>}
                      <div className="mt-3 flex gap-2">
                        <button data-testid={`special-edit-${p.id}`} onClick={() => setSpecialEditor({
                          id: p.id, title: p.name || p.title || "", description: p.description || "",
                          lunch_date: p.scheduled_date || p.lunch_date || "", image_url: p.image_url || "",
                          items: p.items || [], price: p.price || "", max_orders: p.capacity || p.max_orders || "",
                          display_until: p.display_until || "", nutrition: p.nutrition || null,
                        })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-cream-200 text-ink-700 text-xs font-semibold hover:bg-cream-300">
                          <Edit3 size={12} /> Edit
                        </button>
                        <button data-testid={`special-delete-${p.id}`} onClick={async () => {
                          if (!window.confirm(`Delete "${p.name}"?`)) return;
                          try { await api.delete(`/specials/${p.id}`); toast.success("Deleted"); load(); } catch(e) { toast.error(formatErr(e)); }
                        }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100">
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                <div key={p.id} className="bg-white rounded-2xl shadow-soft overflow-hidden flex flex-col">
                  {p.image_url && (
                    <div className="h-32 overflow-hidden">
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-brand">{p.type}</span>
                        {p.type === "daily" && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-bold">PRE-ORDER</span>}
                        {(p.type === "monthly" || p.type === "quarterly" || p.type === "term") && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">SUBSCRIPTION</span>}
                        {p.type === "term" && p.term && <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[9px] font-bold uppercase tracking-wider">{p.term}</span>}
                      </div>
                      <div className="font-heading text-xl font-extrabold text-ink leading-tight">{p.name}</div>
                    </div>
                    {Number(p.price) > 0 && (
                      <div className="text-right">
                        <div className="font-heading text-2xl font-extrabold text-ink">{inr(p.price)}</div>
                        <div className="text-[10px] text-ink-500 uppercase tracking-wider">{p.type === "daily" ? "per order" : `${p.duration_days || 30} days`}</div>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-ink-600 mt-3">{p.description || "—"}</p>
                  <div className="mt-3">{slotsBadge(p.slots)}</div>
                  {p.non_veg_option ? <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-amber-700"><Tag size={10} /> Non-veg lunch enabled</div> : null}
                  {p.applicable_grades?.length > 0 && <div className="mt-1 text-[10px] text-ink-500">Grades: {p.applicable_grades.join(", ")}</div>}
                  <div className="mt-4 pt-4 border-t border-border flex gap-2">
                    <button onClick={() => setEditor({ open: true, isNew: false, draft: { ...p } })}
                      data-testid={`meal-plan-edit-${p.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-full bg-cream-200 text-ink-700 font-semibold text-xs hover:bg-cream-300">
                      <Edit3 size={12} /> Edit
                    </button>
                    <button onClick={() => { setPendingAssignPlanId(p.id); setTab("admin"); }}
                      data-testid={`meal-plan-assign-${p.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-full bg-brand-50 text-brand-700 font-semibold text-xs hover:bg-brand-100">
                      <UserPlus2 size={12} /> Assign
                    </button>
                    <button onClick={() => deletePlan(p)} data-testid={`meal-plan-delete-${p.id}`}
                      className="px-3 py-2 rounded-full bg-red-50 text-red-700 hover:bg-red-100">
                      <Trash2 size={12} />
                    </button>
                  </div>
                  </div>
                </div>
              ))}
            </div>
            )}
            </>
            );
          })()
        ) : tab === "subs" ? (
          <>
            {/* Status filter chips */}
            <div className="flex flex-wrap gap-2 mb-3" data-testid="subs-status-chips">
              {[
                { id: "all",      label: "All",       cls: "bg-ink text-white" },
                { id: "active",   label: "Active",    cls: "bg-emerald-600 text-white" },
                { id: "upcoming", label: "Upcoming",  cls: "bg-blue-600 text-white" },
                { id: "expired",  label: "Expired",   cls: "bg-slate-500 text-white" },
                { id: "paused",   label: "Paused",    cls: "bg-amber-500 text-white" },
              ].map(c => (
                <button
                  key={c.id}
                  data-testid={`subs-chip-${c.id}`}
                  onClick={() => setSubStatusFilter(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${subStatusFilter === c.id ? c.cls : "bg-cream-200 text-ink-700 hover:bg-cream-300"}`}
                >
                  {c.label}
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] ${subStatusFilter === c.id ? "bg-white/20" : "bg-white text-ink-600"}`}>
                    {subStatusCounts[c.id] ?? 0}
                  </span>
                </button>
              ))}
            </div>

            {/* Search and Filters Bar */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-soft space-y-3">
              <div className="flex flex-wrap gap-3 items-end">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500 block mb-1">Search</label>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                    <input
                      type="text"
                      data-testid="subs-search"
                      placeholder="Student name, ID, plan..."
                      value={subSearch}
                      onChange={e => setSubSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 rounded-xl border border-border text-sm focus:border-brand focus:outline-none"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {/* Grade Filter */}
                <select value={subFilters.grade} onChange={e => setSubFilters(f => ({ ...f, grade: e.target.value }))}
                  className="px-3 py-2 rounded-xl border border-border text-sm focus:border-brand focus:outline-none" data-testid="subs-filter-grade">
                  <option value="">All Grades</option>
                  {[...new Set(subs.map(s => s.student_grade).filter(Boolean))].sort().map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                {/* Plan Filter */}
                <select value={subFilters.plan} onChange={e => setSubFilters(f => ({ ...f, plan: e.target.value }))}
                  className="px-3 py-2 rounded-xl border border-border text-sm focus:border-brand focus:outline-none" data-testid="subs-filter-plan">
                  <option value="">All Plans</option>
                  {[...new Set(subs.map(s => s.plan_name).filter(Boolean))].sort().map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {/* Meal Tag Filter */}
                <select value={subFilters.slot} onChange={e => setSubFilters(f => ({ ...f, slot: e.target.value }))}
                  className="px-3 py-2 rounded-xl border border-border text-sm focus:border-brand focus:outline-none" data-testid="subs-filter-slot">
                  <option value="">All Meal Tags</option>
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="snacks">Snacks</option>
                  <option value="dinner">Dinner</option>
                </select>
                {/* Term Filter */}
                <select value={subFilters.term} onChange={e => setSubFilters(f => ({ ...f, term: e.target.value }))}
                  className="px-3 py-2 rounded-xl border border-border text-sm focus:border-brand focus:outline-none" data-testid="subs-filter-term">
                  <option value="">All Terms</option>
                  <option value="Term I">Term I</option>
                  <option value="Term II">Term II</option>
                  <option value="Term III">Term III</option>
                </select>
              </div>
            </div>
            
            {/* Filtered subscriptions */}
            {filteredSubs.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <AlertTriangle size={32} className="mx-auto text-ink-400 mb-3" />
                <div className="font-heading text-xl font-bold text-ink">No subscriptions found</div>
                <p className="text-ink-500 text-sm mt-2">Try adjusting your search or filters, or assign a plan from the Plans tab.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-soft overflow-x-auto">
                <table className="w-full text-sm" data-testid="active-subs-table">
                  <thead className="bg-cream-200 text-left text-[11px] uppercase tracking-wider text-ink-600">
                    <tr>
                      <th className="px-4 py-3">Student</th>
                      <th className="px-4 py-3">Student ID</th>
                      <th className="px-4 py-3">Grade</th>
                      <th className="px-4 py-3">Plan</th>
                      <th className="px-4 py-3">Meal Tags</th>
                      <th className="px-4 py-3">Term</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubs.map(sp => (
                      <tr key={sp.student_plan_id} 
                          onClick={() => openConsumption(sp)}
                          className={`border-t border-border hover:bg-cream-50 ${sp.expiring_soon ? "bg-amber-50" : ""} ${(sp.live_status || sp.status) === "paused" ? "bg-slate-50 opacity-70" : ""} ${(sp.live_status || sp.status) === "expired" ? "opacity-60" : ""} ${(sp.live_status || sp.status) === "upcoming" ? "cursor-default" : "cursor-pointer"}`}
                          data-testid={`sub-row-${sp.student_plan_id}`}>
                        <td className="px-4 py-3 font-semibold text-ink">{sp.student_name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-600">{sp.student_id_display || "—"}</td>
                        <td className="px-4 py-3 text-ink-600">{sp.student_grade || "—"}</td>
                        <td className="px-4 py-3 text-ink-700">
                          {sp.plan_name}
                          {sp.non_veg_option && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold">NV</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(sp.slots || []).map(slot => (
                              <span key={slot} className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[9px] font-bold uppercase">{slot}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-semibold text-ink-700">{sp.term || "—"}</td>
                        <td className="px-4 py-3">
                          {(() => {
                            const live = sp.live_status || sp.status;
                            const cls = live === "active" ? "bg-emerald-100 text-emerald-700"
                              : live === "upcoming" ? "bg-blue-100 text-blue-700"
                              : live === "expired" ? "bg-slate-200 text-slate-600"
                              : live === "paused" ? "bg-amber-100 text-amber-700"
                              : "bg-ink-100 text-ink-600";
                            return (
                              <span data-testid={`sub-status-${sp.student_plan_id}`} className={`px-2 py-1 rounded-full text-[10px] font-bold ${cls}`}>
                                {(live || "").toUpperCase()}
                              </span>
                            );
                          })()}
                          {sp.expiring_soon && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-200 text-[9px] font-extrabold text-amber-800">EXPIRING</span>}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          {(sp.live_status || sp.status) === "expired" ? (
                            <span className="text-[10px] uppercase tracking-wider text-ink-400">—</span>
                          ) : (sp.live_status || sp.status) === "paused" ? (
                            <button onClick={() => pauseSub(sp, "resume")}
                              data-testid={`resume-sub-${sp.student_plan_id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold hover:bg-emerald-200">
                              <Play size={10} /> Resume
                            </button>
                          ) : (
                            <button onClick={() => pauseSub(sp, "pause")}
                              data-testid={`pause-sub-${sp.student_plan_id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-cream-200 text-ink-700 text-xs font-semibold hover:bg-cream-300">
                              <Pause size={10} /> Pause
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}

        {/* Admin actions: search student, assign plan, top-up wallet */}
        {tab === "admin" && (
          <AdminStudentActions plans={plans} onDone={load} pendingPlanId={pendingAssignPlanId} clearPending={() => setPendingAssignPlanId("")} />
        )}
      </div>

      {/* Plan editor */}
      {editor.open && editor.draft ? (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40 flex items-end sm:items-center justify-center p-4">
          <div className="bg-cream-100 rounded-2xl w-full max-w-2xl shadow-tall overflow-hidden">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-brand">{editor.isNew ? "New plan" : "Edit plan"}</div>
                <div className="font-heading text-xl font-extrabold text-ink">Configure meal plan</div>
              </div>
              <button onClick={() => setEditor({ open: false, draft: null, isNew: true })} className="p-2 rounded-full hover:bg-cream-200"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <FormRow label="Plan name">
                <input data-testid="plan-name" value={editor.draft.name} onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, name: e.target.value } }))}
                  placeholder="e.g. Lunch-only Monthly" className="w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none" />
              </FormRow>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Type">
                  <select value={editor.draft.type} onChange={e => {
                    const t = e.target.value;
                    setEditor(s => {
                      const sd = s.draft.start_date;
                      const ed = s.draft.end_date;
                      let dur;
                      if (t === "term") {
                        dur = daysBetween(sd, ed) || s.draft.duration_days || 30;
                      } else {
                        dur = t === "daily" ? 1 : t === "weekly" ? 7 : 30;
                      }
                      return { ...s, draft: { ...s.draft, type: t, duration_days: dur, term: t === "term" ? (s.draft.term || "") : "" } };
                    });
                  }}
                    className="w-full rounded-xl border border-border px-3 py-2 bg-white">
                    {TYPE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </FormRow>
                {editor.draft.type === "monthly" ? (
                  <FormRow label="Billing">
                    <div className="w-full rounded-xl border border-border px-3 py-2 bg-cream-100 text-xs text-ink-500">
                      Billed by the school directly. No price collected here.
                    </div>
                  </FormRow>
                ) : editor.draft.type === "term" ? (
                  <FormRow label="Term *">
                    <select data-testid="plan-term" value={editor.draft.term || ""} onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, term: e.target.value } }))}
                      className="w-full rounded-xl border border-border px-3 py-2 bg-white">
                      <option value="">— Select term —</option>
                      {schoolTerms.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <div className="text-[10px] text-ink-500 mt-1">Manage the list in School Master → Terms.</div>
                  </FormRow>
                ) : (
                  <FormRow label={<><IndianRupee size={12} className="inline" /> Price</>}>
                    <input data-testid="plan-price" type="number" min="0" value={editor.draft.price} onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, price: e.target.value } }))}
                      className="w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none" />
                  </FormRow>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Duration (days)">
                  <input
                    type="number"
                    min="1"
                    value={editor.draft.duration_days}
                    disabled={editor.draft.type === "term"}
                    onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, duration_days: e.target.value } }))}
                    className={`w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none ${editor.draft.type === "term" ? "bg-cream-100 text-ink-500 cursor-not-allowed" : ""}`}
                  />
                  {editor.draft.type === "term" && (
                    <div className="text-[10px] text-ink-500 mt-1">Auto-calculated from the term's start &amp; end dates.</div>
                  )}
                </FormRow>
                <FormRow label="Items per day">
                  <input type="number" min="1" value={editor.draft.items_per_day} onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, items_per_day: e.target.value } }))}
                    className="w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none" />
                </FormRow>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Start date *">
                  <input data-testid="plan-start-date" type="date" required value={editor.draft.start_date || ""} onChange={e => setEditor(s => {
                    const sd = e.target.value;
                    const ed = s.draft.end_date;
                    const dur = (s.draft.type === "term" && sd && ed) ? daysBetween(sd, ed) : s.draft.duration_days;
                    return { ...s, draft: { ...s.draft, start_date: sd, duration_days: dur } };
                  })}
                    className="w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none" />
                </FormRow>
                <FormRow label="End date *">
                  <input data-testid="plan-end-date" type="date" required value={editor.draft.end_date || ""} onChange={e => setEditor(s => {
                    const ed = e.target.value;
                    const sd = s.draft.start_date;
                    const dur = (s.draft.type === "term" && sd && ed) ? daysBetween(sd, ed) : s.draft.duration_days;
                    return { ...s, draft: { ...s.draft, end_date: ed, duration_days: dur } };
                  })}
                    className="w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none" />
                </FormRow>
              </div>
              <FormRow label="Variant">
                <select data-testid="plan-variant" value={editor.draft.variant || "standard"} onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, variant: e.target.value } }))}
                  className="w-full rounded-xl border border-border px-3 py-2 bg-white focus:border-brand focus:outline-none">
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="non-veg">Non-veg</option>
                </select>
                <div className="text-[10px] text-ink-500 mt-1">Same slot + same variant cannot overlap in date range</div>
              </FormRow>
              <FormRow label="Applicable grades (optional)">
                <input data-testid="plan-grades" value={(editor.draft.applicable_grades || []).join(", ")}
                  onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, applicable_grades: e.target.value.split(",").map(g => g.trim()).filter(Boolean) } }))}
                  placeholder="e.g. Grade 1, Grade 2, Grade 3 (empty = all grades)"
                  className="w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none" />
                <div className="text-[10px] text-ink-500 mt-1">Comma-separated. Leave empty to allow all grades.</div>
              </FormRow>
              <FormRow label="Slots covered">
                <div className="flex flex-wrap gap-2">
                  {SLOT_OPTIONS.map(s => {
                    const on = editor.draft.slots?.includes(s.id);
                    return (
                      <button key={s.id} type="button" onClick={() => setEditor(st => {
                        const cur = new Set(st.draft.slots || []);
                        cur.has(s.id) ? cur.delete(s.id) : cur.add(s.id);
                        return { ...st, draft: { ...st.draft, slots: Array.from(cur) } };
                      })}
                        data-testid={`plan-slot-${s.id}`}
                        className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${on ? "bg-brand text-white" : "bg-white border border-border text-ink-600"}`}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </FormRow>
              {editor.draft.slots?.includes("lunch") ? (
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!editor.draft.non_veg_option}
                    onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, non_veg_option: e.target.checked } }))} />
                  <span className="text-sm text-ink-700">Non-veg lunch option enabled</span>
                </label>
              ) : null}
              <FormRow label="Plan image">
                <div className="flex items-center gap-3">
                  {editor.draft.image_url && (
                    <img src={editor.draft.image_url} alt="Plan" className="w-16 h-16 rounded-xl object-cover border border-border" />
                  )}
                  <div className="flex-1">
                    <input data-testid="plan-image-url" value={editor.draft.image_url || ""} onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, image_url: e.target.value } }))}
                      placeholder="Paste image URL or upload below" className="w-full rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                    <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cream-200 text-ink-700 text-xs font-semibold cursor-pointer hover:bg-cream-300">
                      Upload image
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => setEditor(s => ({ ...s, draft: { ...s.draft, image_url: reader.result } }));
                        reader.readAsDataURL(file);
                      }} />
                    </label>
                  </div>
                </div>
              </FormRow>
              <FormRow label="Description">
                <textarea rows={2} value={editor.draft.description} onChange={e => setEditor(s => ({ ...s, draft: { ...s.draft, description: e.target.value } }))}
                  placeholder="What's included? Any T&Cs?" className="w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none" />
              </FormRow>
              {/* Nutrition - Commented out per user request
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-500">Nutrition (per serving)</span>
                  <button type="button" data-testid="ai-nutrition-btn" onClick={async () => {
                    if (!editor.draft?.name?.trim()) return toast.error("Add a plan name first");
                    try {
                      const { data } = await api.post("/ai/nutrition/suggest", { name: editor.draft.name, description: editor.draft.description || "" });
                      setEditor(s => ({ ...s, draft: { ...s.draft, nutrition: data } }));
                      toast.success("Nutrition values suggested by AI");
                    } catch (e) { toast.error("AI suggestion failed"); }
                  }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-50 text-brand-700 text-[11px] font-semibold hover:bg-brand-100">
                    <Sparkles size={12} /> AI Suggest
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[["calories","Calories (kcal)"],["protein","Protein (g)"],["carbs","Carbs (g)"],["fat","Fat (g)"],["fiber","Fiber (g)"],["serving_size","Serving size"]].map(([k,label]) => (
                    <div key={k}>
                      <label className="text-[10px] text-ink-500">{label}</label>
                      <input data-testid={`nutrition-${k}`} value={editor.draft.nutrition?.[k] ?? ""} onChange={e => {
                        const val = k === "serving_size" ? e.target.value : (e.target.value === "" ? "" : Number(e.target.value));
                        setEditor(s => ({ ...s, draft: { ...s.draft, nutrition: { ...(s.draft.nutrition || {}), [k]: val } } }));
                      }} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm focus:border-brand focus:outline-none" />
                    </div>
                  ))}
                </div>
              </div>
              */}
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button onClick={() => setEditor({ open: false, draft: null, isNew: true })}
                className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="plan-save" onClick={savePlan}
                className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95">
                {editor.isNew ? "Create plan" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Assign plan to student */}
      {assigner.open && assigner.plan ? (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40 flex items-end sm:items-center justify-center p-4">
          <div className="bg-cream-100 rounded-2xl w-full max-w-lg shadow-tall overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-brand">Assign subscription</div>
                <div className="font-heading text-xl font-extrabold text-ink">{assigner.plan.name}</div>
              </div>
              <button onClick={() => setAssigner({ ...assigner, open: false })} className="p-2 rounded-full hover:bg-cream-200"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Grade filter - Checkbox list */}
              <FormRow label="Filter by grade">
                {(() => {
                  const allGrades = [...new Set(students.map(s => s.grade).filter(Boolean))].sort();
                  const sel = assigner.grades || [];
                  return (
                    <>
                      <div data-testid="assign-grade-select" className="w-full rounded-xl border border-border bg-white max-h-32 overflow-y-auto p-2 space-y-1">
                        {allGrades.length === 0 && <div className="text-xs text-ink-400 px-2 py-1">No grades found.</div>}
                        {allGrades.map(g => {
                          const checked = sel.includes(g);
                          return (
                            <label key={g} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-cream-100 ${checked ? "bg-cream-100" : ""}`}>
                              <input type="checkbox" checked={checked} onChange={e => {
                                const next = e.target.checked ? [...new Set([...sel, g])] : sel.filter(x => x !== g);
                                setAssigner(a => ({ ...a, grades: next }));
                              }} className="accent-brand" />
                              <span className="text-sm text-ink">{g}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="text-[10px] text-ink-500 mt-1">Tick to filter. {sel.length} selected</div>
                    </>
                  );
                })()}
              </FormRow>
              {/* Students multiselect - Checkbox list */}
              <FormRow label="Select students">
                {(() => {
                  const sel = assigner.studentIds || [];
                  const filteredStudents = students.filter(s => !(assigner.grades || []).length || (assigner.grades || []).includes(s.grade));
                  // Build a set of student IDs already actively assigned to THIS plan
                  const alreadyAssigned = new Set(
                    (subs || [])
                      .filter(sp => sp.plan_id === assigner.plan?.id && (sp.status === "active" || sp.status === "paused"))
                      .map(sp => sp.student_id)
                  );
                  return (
                    <>
                      <div data-testid="assign-student-select" className="w-full rounded-xl border border-border bg-white max-h-60 overflow-y-auto p-2 space-y-1">
                        {filteredStudents.length === 0 && <div className="text-xs text-ink-400 px-2 py-1">No students match the selected grades.</div>}
                        {filteredStudents.map(s => {
                          const checked = sel.includes(s.id);
                          const assigned = alreadyAssigned.has(s.id);
                          return (
                            <label key={s.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${assigned ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-cream-100"} ${checked ? "bg-cream-100" : ""}`}>
                              <input
                                type="checkbox"
                                checked={checked || assigned}
                                disabled={assigned}
                                onChange={e => {
                                  if (assigned) return;
                                  const next = e.target.checked ? [...new Set([...sel, s.id])] : sel.filter(x => x !== s.id);
                                  setAssigner(a => ({ ...a, studentIds: next }));
                                }}
                                className="accent-brand"
                              />
                              <span className="text-sm text-ink">{s.name}</span>
                              <span className="text-xs text-ink-500">— {s.grade || "—"} · {s.student_id || s.email}</span>
                              {assigned && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Already assigned</span>}
                            </label>
                          );
                        })}
                      </div>
                      <div className="text-[10px] text-ink-500 mt-1">Tick the students to assign. {sel.length} new · {filteredStudents.filter(s => alreadyAssigned.has(s.id)).length} already on this plan</div>
                    </>
                  );
                })()}
              </FormRow>
              {/* Select all / deselect */}
              <div className="flex gap-2">
                <button type="button" onClick={() => {
                  const alreadyAssigned = new Set(
                    (subs || [])
                      .filter(sp => sp.plan_id === assigner.plan?.id && (sp.status === "active" || sp.status === "paused"))
                      .map(sp => sp.student_id)
                  );
                  const filtered = students.filter(s =>
                    (!(assigner.grades || []).length || (assigner.grades || []).includes(s.grade))
                    && !alreadyAssigned.has(s.id)
                  );
                  setAssigner(a => ({ ...a, studentIds: filtered.map(s => s.id) }));
                }} className="text-xs font-semibold text-brand">Select all</button>
                <button type="button" onClick={() => setAssigner(a => ({ ...a, studentIds: [] }))} className="text-xs font-semibold text-ink-500">Deselect all</button>
              </div>
              {/* Bulk upload */}
              <div className="border-t border-border pt-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-2">Or bulk upload via Excel/CSV</div>
                <div className="flex items-center gap-2">
                  <button type="button" data-testid="assign-bulk-btn" onClick={() => assigner.bulkRef?.current?.click()}
                    className="px-4 py-2 rounded-xl bg-ink text-white text-xs font-semibold hover:bg-ink-700">Upload CSV</button>
                  <a href="/docs/assign-template.csv" download className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-cream-200 text-ink-700 text-xs font-semibold hover:bg-cream-300">
                    <Download size={12} /> Template
                  </a>
                  <input ref={el => { if (el) assigner.bulkRef = { current: el }; }} type="file" accept=".csv,.xlsx" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const text = await file.text();
                    const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
                    const headers = lines[0]?.split(",").map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, "")) || [];
                    const rows = lines.slice(1).map(line => {
                      const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
                      const obj = {};
                      headers.forEach((h, i) => { obj[h] = cols[i] || ""; });
                      return obj;
                    });
                    // Extract student IDs from either student_id column or first column
                    const ids = rows.map(r => r.student_id || Object.values(r)[0]).filter(Boolean);
                    // Match student_ids to actual students
                    const matched = ids.map(sid => students.find(s => s.student_id === sid || s.id === sid || s.email === sid)).filter(Boolean);
                    setAssigner(a => ({ ...a, studentIds: [...new Set([...(a.studentIds || []), ...matched.map(s => s.id)])] }));
                    toast.success(`Matched ${matched.length} of ${ids.length} students from CSV`);
                    e.target.value = "";
                  }} />
                </div>
                <div className="text-[10px] text-ink-400 mt-2">
                  Template columns: student_id, student_name, student_grade, plan_name, plan_term, status
                </div>
              </div>
              <FormRow label="Note (optional)">
                <textarea rows={2} value={assigner.note}
                  onChange={e => setAssigner(a => ({ ...a, note: e.target.value }))}
                  placeholder="Any context?" className="w-full rounded-xl border border-border px-3 py-2" />
              </FormRow>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button onClick={() => setAssigner({ ...assigner, open: false })}
                className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="assign-submit" onClick={submitAssign}
                className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95">
                Assign {(assigner.studentIds || []).length} student{(assigner.studentIds || []).length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Special Editor Popup */}
      {specialEditor && (
        <InlineSpecialEditor data={specialEditor} onClose={() => setSpecialEditor(null)} onSaved={() => { setSpecialEditor(null); load(); }} />
      )}
      
      {/* Consumption Modal */}
      {consumptionModal.open && (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-cream-100 rounded-2xl w-full max-w-lg shadow-tall overflow-hidden">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-brand">Consumption Details</div>
                <div className="font-heading text-xl font-extrabold text-ink">
                  {consumptionModal.data?.student_name || "Loading..."}
                </div>
              </div>
              <button onClick={() => setConsumptionModal({ open: false, data: null, loading: false })} 
                className="p-2 rounded-full hover:bg-cream-200" data-testid="close-consumption-modal"><X size={16} /></button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {consumptionModal.loading ? (
                <div className="text-center py-8 text-ink-500">Loading consumption data...</div>
              ) : consumptionModal.data ? (
                <div className="space-y-4">
                  {/* Student & Plan Info */}
                  <div className="bg-white rounded-xl p-4 border border-border">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-ink-500">Student ID:</span> <b className="text-ink">{consumptionModal.data.student_id_display}</b></div>
                      <div><span className="text-ink-500">Grade:</span> <b className="text-ink">{consumptionModal.data.student_grade || "—"}</b></div>
                      <div><span className="text-ink-500">Plan:</span> <b className="text-ink">{consumptionModal.data.plan_name}</b></div>
                      <div><span className="text-ink-500">Term:</span> <b className="text-ink">{consumptionModal.data.term}</b></div>
                      <div><span className="text-ink-500">Period:</span> <b className="text-ink">{consumptionModal.data.start_date} → {consumptionModal.data.end_date}</b></div>
                      <div><span className="text-ink-500">Status:</span> 
                        <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          consumptionModal.data.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                        }`}>{consumptionModal.data.status?.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Subscribed Slots */}
                  <div className="text-xs text-ink-600">
                    <span className="font-semibold">Subscribed Meals:</span>{" "}
                    {(consumptionModal.data.subscribed_slots || []).map(s => (
                      <span key={s} className="ml-1 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold uppercase">{s}</span>
                    ))}
                  </div>
                  
                  {consumptionModal.data.mode === "history" ? (
                    <>
                      {/* Aggregated stats */}
                      <div className="grid grid-cols-3 gap-3 mt-4">
                        <div className="rounded-xl bg-cream-100 border border-border p-3">
                          <div className="text-[10px] uppercase font-bold tracking-wider text-ink-500">Total slots</div>
                          <div className="text-xl font-bold text-ink">{consumptionModal.data.total_slots}</div>
                        </div>
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                          <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Consumed</div>
                          <div className="text-xl font-bold text-emerald-700">{consumptionModal.data.consumed_slots}</div>
                        </div>
                        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                          <div className="text-[10px] uppercase font-bold tracking-wider text-amber-700">Missed</div>
                          <div className="text-xl font-bold text-amber-700">{consumptionModal.data.missed_slots}</div>
                        </div>
                      </div>

                      <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mt-4 mb-2">
                        Day-by-day consumption ({(consumptionModal.data.history || []).length} day{(consumptionModal.data.history || []).length === 1 ? "" : "s"} with at least one tap)
                      </div>
                      {(consumptionModal.data.history || []).length === 0 ? (
                        <div className="rounded-xl bg-cream-100 border border-border p-6 text-center text-sm text-ink-500">
                          No RFID taps recorded for this subscription window.
                        </div>
                      ) : (
                        <div className="rounded-xl border border-border overflow-hidden" data-testid="history-table">
                          <table className="w-full text-sm">
                            <thead className="bg-cream-100 text-[10px] uppercase font-bold tracking-wider text-ink-500">
                              <tr>
                                <th className="px-3 py-2 text-left">Date</th>
                                {(consumptionModal.data.subscribed_slots || []).map(s => (
                                  <th key={s} className="px-3 py-2 text-left capitalize">{s}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(consumptionModal.data.history || []).map(d => (
                                <tr key={d.date} className="border-t border-border">
                                  <td className="px-3 py-2 text-xs text-ink-700 font-mono">{d.date}</td>
                                  {(consumptionModal.data.subscribed_slots || []).map(s => {
                                    const v = d.slots?.[s.toLowerCase()];
                                    return (
                                      <td key={s} className="px-3 py-2 text-xs">
                                        {v ? (
                                          <span className="inline-flex items-center gap-1 text-emerald-700">
                                            <span>✓</span>
                                            <span>{v.time ? new Date(v.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true }) : ""}</span>
                                          </span>
                                        ) : (
                                          <span className="text-amber-600">—</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Consumption by Meal Tag (today) */}
                      <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mt-4 mb-2">
                        Meal Consumption for {consumptionModal.data.date}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {["breakfast", "lunch", "snacks", "dinner"].map(slot => {
                          const c = consumptionModal.data.consumption?.[slot];
                          const status = c?.status;
                          return (
                            <div key={slot} className={`rounded-xl p-4 border ${
                              status === "consumed" ? "bg-emerald-50 border-emerald-200" :
                              status === "not_consumed" ? "bg-amber-50 border-amber-200" :
                              "bg-slate-50 border-slate-200"
                            }`} data-testid={`consumption-${slot}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-bold text-ink capitalize">{slot}</span>
                                <span className={`text-lg font-bold ${
                                  status === "consumed" ? "text-emerald-600" :
                                  status === "not_consumed" ? "text-amber-600" : "text-slate-400"
                                }`}>
                                  {status === "consumed" ? "✓" : status === "not_consumed" ? "−" : "NA"}
                                </span>
                              </div>
                              <div className="text-[10px] text-ink-500">
                                {status === "NA" ? "Not subscribed" :
                                 status === "consumed" ? (
                                   <>Consumed at {c.time ? new Date(c.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true }) : "—"}</>
                                 ) : "Not yet consumed"}
                              </div>
                              {status === "consumed" && c.items?.length > 0 && (
                                <div className="mt-2 text-[10px] text-ink-600">
                                  {c.items.map((it, i) => <span key={i} className="mr-1">{it.name}{i < c.items.length - 1 ? "," : ""}</span>)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-ink-500">No data available</div>
              )}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function InlineSpecialEditor({ data: initial, onClose, onSaved }) {
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("image_url", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!data.title?.trim()) return toast.error("Title required");
    if (!data.lunch_date) return toast.error("Lunch date required");
    setBusy(true);
    const payload = {
      title: data.title.trim(), description: data.description || "", image_url: data.image_url || "",
      lunch_date: data.lunch_date, items: (data.items || []).filter(i => i.name?.trim()).map(i => ({ name: i.name.trim(), qty: i.qty || 1 })),
      price: Number(data.price) || 0, max_orders: data.max_orders ? Number(data.max_orders) : null,
      display_until: data.display_until || null,
    };
    try {
      if (data.id) await api.put(`/specials/${data.id}`, payload);
      else await api.post("/specials", payload);
      toast.success(data.id ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-center justify-center px-4 overflow-y-auto py-6" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-lg my-auto shadow-tall max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">{data.id ? "Edit special" : "New special"}</div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-cream-200"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-ink-500">Title *</label>
            <input data-testid="sp-title" value={data.title} onChange={e => set("title", e.target.value)} placeholder="Diwali Special Thali" className="w-full mt-1 rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-ink-500">Description</label>
            <textarea value={data.description} onChange={e => set("description", e.target.value)} rows={2} placeholder="A festive thali with sweets..." className="w-full mt-1 rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase text-ink-500">Lunch date *</label>
              <input data-testid="sp-date" type="date" value={data.lunch_date} onChange={e => set("lunch_date", e.target.value)} className="w-full mt-1 rounded-xl border border-border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-ink-500">Image</label>
              <div className="mt-1 flex items-center gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-2 rounded-xl bg-cream-200 text-ink-700 text-xs font-semibold hover:bg-cream-300">Upload</button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                {data.image_url && <span className="text-[10px] text-emerald-700">Set</span>}
              </div>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-ink-500">Items ({data.items?.length || 0})</label>
            <div className="mt-1 space-y-1.5">
              {(data.items || []).map((it, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input type="text" placeholder="Item name" value={it.name || ""} onChange={e => { const items = [...data.items]; items[idx] = { ...items[idx], name: e.target.value }; set("items", items); }}
                    className="flex-1 rounded-lg border border-border px-2 py-1.5 text-sm" />
                  <input type="number" min={1} value={it.qty || 1} onChange={e => { const items = [...data.items]; items[idx] = { ...items[idx], qty: Number(e.target.value) || 1 }; set("items", items); }}
                    className="w-14 rounded-lg border border-border px-2 py-1.5 text-sm" />
                  <button onClick={() => set("items", data.items.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                </div>
              ))}
              <button type="button" onClick={() => set("items", [...(data.items || []), { name: "", qty: 1 }])} className="text-xs font-semibold text-brand">+ Add item</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase text-ink-500">Price (₹) *</label>
              <input data-testid="sp-price" type="number" value={data.price} onChange={e => set("price", e.target.value)} className="w-full mt-1 rounded-xl border border-border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-ink-500">Max covers</label>
              <input type="number" value={data.max_orders || ""} onChange={e => set("max_orders", e.target.value)} placeholder="Unlimited" className="w-full mt-1 rounded-xl border border-border px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-ink-500">Display until (order cutoff)</label>
            <input data-testid="sp-display-until" type="date" value={data.display_until || ""} onChange={e => set("display_until", e.target.value)} className="w-full mt-1 rounded-xl border border-border px-3 py-2 text-sm" />
            <div className="text-[10px] text-ink-400 mt-0.5">After this date, parents cannot order</div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
            <button data-testid="sp-save" onClick={save} disabled={busy} className="px-5 py-2 rounded-xl bg-brand text-white font-semibold text-sm disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function SpecialsTab() {
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/specials").then(r => { setSpecials(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-ink-500 text-sm py-8 text-center">Loading specials…</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-ink-500">Festival and special menus are managed here. These appear as one-time bookable items for parents.</p>
        <a href="/admin/specials" data-testid="manage-specials-link"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-ink text-white font-semibold text-sm hover:bg-ink-700">
          <Plus size={14} /> Manage specials
        </a>
      </div>
      {specials.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-8 text-center text-ink-500">No specials created yet. Click "Manage specials" to add.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {specials.map(s => (
            <div key={s.id} className="bg-white rounded-2xl border border-border overflow-hidden" data-testid={`special-card-${s.id}`}>
              {s.image_url && <img src={s.image_url} alt={s.name} className="w-full h-32 object-cover" />}
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${s.status === "open" ? "bg-emerald-200 text-emerald-800" : "bg-cream-200 text-ink-700"}`}>{s.status}</span>
                  <span className="text-xs text-ink-500">{s.scheduled_date}</span>
                </div>
                <div className="font-heading font-bold text-ink mt-1">{s.name}</div>
                <div className="text-sm text-ink-500 mt-0.5">₹{s.price} · {s.booked || 0} booked{s.capacity ? ` / ${s.capacity}` : ""}</div>
                {s.nutrition && <div className="text-[10px] text-ink-400 mt-1">{s.nutrition.calories} kcal · {s.nutrition.protein}g protein</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminStudentActions({ plans, onDone, pendingPlanId, clearPending }) {
  const [searchId, setSearchId] = useState("");
  const [foundStudent, setFoundStudent] = useState(null);
  const [searching, setSearching] = useState(false);
  const [assignPlanId, setAssignPlanId] = useState("");
  const [assignStart, setAssignStart] = useState("");
  const [assignEnd, setAssignEnd] = useState("");
  const [topupAmount, setTopupAmount] = useState("");
  const [busy, setBusy] = useState(false);

  // Pre-select a plan when the admin clicked "Assign" on the Plans tab
  useEffect(() => {
    if (pendingPlanId) {
      setAssignPlanId(pendingPlanId);
      // Auto-fill dates from the plan if available
      const p = (plans || []).find(x => x.id === pendingPlanId);
      if (p) {
        if (p.start_date) setAssignStart(p.start_date);
        if (p.end_date) setAssignEnd(p.end_date);
      }
      clearPending?.();
    }
  }, [pendingPlanId, plans, clearPending]);

  const searchStudent = async () => {
    if (!searchId.trim()) return toast.error("Enter a student ID");
    setSearching(true); setFoundStudent(null);
    try {
      const { data } = await api.get(`/admin/users?role=student`);
      const match = (data || []).find(s =>
        s.student_id === searchId.trim() || s.id === searchId.trim() || s.email?.toLowerCase() === searchId.trim().toLowerCase()
      );
      if (match) setFoundStudent(match);
      else toast.error("No student found with that ID");
    } catch { toast.error("Search failed"); }
    setSearching(false);
  };

  const assignPlan = async () => {
    if (!foundStudent || !assignPlanId) return toast.error("Select a plan");
    setBusy(true);
    try {
      const payload = { student_id: foundStudent.id, plan_id: assignPlanId };
      if (assignStart) payload.start_date = assignStart;
      if (assignEnd) payload.end_date = assignEnd;
      const { data } = await api.post("/meal-plans/admin-assign", payload);
      toast.success(`Assigned to ${data.student_plan?.student_name}`);
      onDone();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const topupWallet = async () => {
    if (!foundStudent || !topupAmount || Number(topupAmount) <= 0) return toast.error("Enter valid amount");
    setBusy(true);
    try {
      const { data } = await api.post("/meal-plans/admin-topup", { student_id: foundStudent.id, amount: Number(topupAmount) });
      toast.success(`Topped up ${data.student_name}. New balance: ₹${data.new_balance}`);
      setTopupAmount("");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      {assignPlanId && (() => {
        const p = (plans || []).find(x => x.id === assignPlanId);
        if (!p) return null;
        return (
          <div data-testid="preselected-plan-banner" className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3 flex items-center justify-between">
            <div className="text-sm text-emerald-800">
              <span className="font-bold">Assigning plan:</span> {p.name} <span className="text-emerald-600">({p.type}{p.term ? ` · ${p.term}` : ""})</span>
            </div>
            <button onClick={() => { setAssignPlanId(""); setAssignStart(""); setAssignEnd(""); }} className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline">Clear</button>
          </div>
        );
      })()}
      {/* Bulk Upload */}
      <BulkAssignSection plans={plans} onDone={onDone} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search */}
        <div className="bg-white rounded-2xl border border-border p-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-3">Find student</div>
        <div className="flex gap-2">
          <input data-testid="admin-search-student" value={searchId} onChange={e => setSearchId(e.target.value)}
            placeholder="Student ID, email, or internal ID" onKeyDown={e => e.key === "Enter" && searchStudent()}
            className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
          <button data-testid="admin-search-btn" onClick={searchStudent} disabled={searching}
            className="px-4 py-2.5 rounded-xl bg-ink text-white font-semibold text-sm disabled:opacity-60">
            <Search size={16} />
          </button>
        </div>
        {foundStudent && (
          <div className="mt-4 p-4 rounded-xl bg-cream-100 border border-border" data-testid="admin-found-student">
            <div className="font-heading font-bold text-ink">{foundStudent.name}</div>
            <div className="text-xs text-ink-500 mt-0.5">{foundStudent.grade || "—"} · {foundStudent.student_id || foundStudent.email} · Wallet: ₹{foundStudent.wallet_balance || 0}</div>
          </div>
        )}
      </div>

      {/* Actions (only visible when student found) */}
      {foundStudent && (
        <div className="space-y-5">
          {/* Assign Plan */}
          <div className="bg-white rounded-2xl border border-border p-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">Assign meal plan</div>
            <select data-testid="admin-assign-plan" value={assignPlanId} onChange={e => {
                const id = e.target.value;
                setAssignPlanId(id);
                const p = (plans || []).find(x => x.id === id);
                // Auto-fill start/end from the chosen plan (overwrite previous picks)
                setAssignStart(p?.start_date || "");
                setAssignEnd(p?.end_date || "");
              }}
              className="w-full rounded-xl border border-border px-3 py-2.5 bg-white text-sm mb-3">
              <option value="">— Select plan —</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.type}{p.term ? ` · ${p.term}` : ""}) · {p.slots?.join(", ")}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="text-[10px] font-bold uppercase text-ink-500">Start</label>
                <input type="date" value={assignStart} onChange={e => setAssignStart(e.target.value)}
                  className="w-full rounded-xl border border-border px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-ink-500">End</label>
                <input type="date" value={assignEnd} onChange={e => setAssignEnd(e.target.value)}
                  className="w-full rounded-xl border border-border px-3 py-2 text-sm" />
              </div>
            </div>
            <button data-testid="admin-assign-btn" onClick={assignPlan} disabled={busy}
              className="w-full px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-60">
              Assign plan to {foundStudent.name}
            </button>
          </div>

          {/* Top-up Wallet */}
          <div className="bg-white rounded-2xl border border-border p-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-3 flex items-center gap-2"><Wallet size={12} /> Top-up student wallet</div>
            <div className="flex gap-2">
              <input data-testid="admin-topup-amount" type="number" min="1" placeholder="Amount ₹"
                value={topupAmount} onChange={e => setTopupAmount(e.target.value)}
                className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
              <button data-testid="admin-topup-btn" onClick={topupWallet} disabled={busy}
                className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-60">
                Top-up
              </button>
            </div>
            <div className="text-[10px] text-ink-500 mt-2">Current balance: ₹{foundStudent.wallet_balance || 0}</div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function BulkAssignSection({ plans, onDone }) {
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileRef = useRef(null);

  const onBulkFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const text = await file.text();
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { toast.error("CSV must have a header row + data"); setBulkBusy(false); return; }
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, ""));
      const rows = lines.slice(1).map(line => {
        const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
        const obj = {};
        headers.forEach((h, i) => { obj[h] = cols[i] || ""; });
        return obj;
      });
      const { data } = await api.post("/meal-plans/bulk-assign", rows);
      setBulkResult(data);
      const msg = data.students_created > 0 
        ? `Assigned ${data.assigned_count} students (${data.students_created} new students created). ${data.error_count} errors.`
        : `Assigned ${data.assigned_count} students. ${data.error_count} errors.`;
      toast.success(msg);
      onDone();
    } catch (err) { toast.error(formatErr(err)); }
    setBulkBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Bulk assign students to meal plan</div>
          <p className="text-xs text-ink-500 mt-1">Upload a CSV with columns: <code className="bg-cream-200 px-1 rounded">student_id, student_name, student_grade, plan_name, plan_term, status</code></p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/docs/assign-template.csv" download className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-cream-200 text-ink-700 font-semibold text-sm hover:bg-cream-300">
            <Download size={14} /> Template
          </a>
          <button data-testid="bulk-assign-btn" onClick={() => fileRef.current?.click()} disabled={bulkBusy}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-semibold text-sm disabled:opacity-60">
            {bulkBusy ? "Processing…" : "Upload CSV"}
          </button>
        </div>
        <input ref={fileRef} type="file" accept=".csv" onChange={onBulkFile} className="hidden" data-testid="bulk-assign-file" />
      </div>
      {bulkResult && (
        <div className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="bulk-assign-result">
          <div className="text-sm font-bold text-emerald-900">
            Assigned: {bulkResult.assigned_count} · 
            {bulkResult.students_created > 0 && <span className="text-blue-700"> New students: {bulkResult.students_created} · </span>}
            Errors: {bulkResult.error_count}
          </div>
          {bulkResult.errors?.length > 0 && (
            <div className="mt-2 text-xs text-amber-900 space-y-0.5 max-h-32 overflow-y-auto">
              {bulkResult.errors.slice(0, 10).map((er, i) => <div key={i}>Row {er.row}: {er.reason}</div>)}
              {bulkResult.errors.length > 10 && <div>…and {bulkResult.errors.length - 10} more</div>}
            </div>
          )}
        </div>
      )}
      <div className="mt-3 text-[10px] text-ink-400">
        CSV example: <code className="bg-cream-200 px-1 rounded">STU001, Aarav Sharma, Grade 5, Regular Meal Plan, Term I, active</code>
      </div>
      <div className="mt-2 text-[10px] text-ink-400">
        <b>Terms:</b> Term I (Jun-Sep), Term II (Oct-Jan), Term III (Feb-May). <b>Status:</b> active or paused.
      </div>
    </div>
  );
}

function EmptyState({ onClick }) {
  return (
    <div className="bg-white rounded-2xl p-12 text-center">
      <Calendar size={32} className="mx-auto text-ink-400 mb-3" />
      <div className="font-heading text-xl font-bold text-ink">No meal plans yet</div>
      <p className="text-ink-500 text-sm mt-2 mb-5">Create your first plan — start with "Lunch only" or "Breakfast + Lunch".</p>
      <button onClick={onClick} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600">
        <Plus size={16} /> Create first plan
      </button>
    </div>
  );
}
